// Language selector component for navbar
// Dropdown with flags and language switching functionality

import React, { useState, useRef, useEffect, useCallback } from 'react'

import { useLanguage, useTranslation } from '@platform/i18n/hooks/useTranslation'
import type { LanguageSelectorProps } from '@platform/i18n/types/i18n.types'

import { settingsService } from '@/platform/profile/services/settings.service'
import { logger } from '@/shared/utils/logger'

// Flag components as SVG
const SpanishFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 16" fill="none">
    <rect y="0" width="24" height="4" fill="#AD1519" />
    <rect y="4" width="24" height="8" fill="#FABD00" />
    <rect y="12" width="24" height="4" fill="#AD1519" />
  </svg>
)

const EnglishFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 16" fill="none">
    <rect width="24" height="16" fill="#012169" />
    <path d="M0 0L24 16M24 0L0 16" stroke="#FFFFFF" strokeWidth="2" />
    <path d="M0 0L24 16M24 0L0 16" stroke="#C8102E" strokeWidth="1" />
    <path d="M12 0V16M0 8H24" stroke="#FFFFFF" strokeWidth="3" />
    <path d="M12 0V16M0 8H24" stroke="#C8102E" strokeWidth="2" />
  </svg>
)

/**
 * Fallback flag icon for any language without a dedicated SVG flag
 * (de, fr, it, pt, nl, ru, zh, ja, …). Defined at module scope so React
 * doesn't see a new component type on every render (issue #330).
 */
const GenericFlag: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="9" strokeWidth="2" />
    <path strokeWidth="2" d="M3 12h18M12 3v18M5 7c4 5 10 5 14 0M5 17c4-5 10-5 14 0" />
  </svg>
)

/** Code → SVG flag map for well-known languages. Defined module-scoped. */
const FlagByCode: Record<string, React.FC<{ className?: string }>> = {
  es: SpanishFlag,
  en: EnglishFlag,
}

/**
 * Language selector component with dropdown functionality
 * Integrates with useLanguage hook for state management
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  showFlags = true,
  showLabels = true,
  variant = 'dropdown',
  size = 'md',
  position = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation(['common'])
  const { currentLanguage, availableLanguages, changeLanguage, isLoading } = useLanguage()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }

    // Return a cleanup function even when isOpen is false
    return () => {}
  }, [isOpen])

  const handleLanguageChange = useCallback(
    async (lng: string) => {
      if (lng === currentLanguage || isLoading) return

      try {
        // Apply the language live (updates i18n + localStorage). Does NOT persist
        // to the backend by itself.
        await changeLanguage(lng)
        setIsOpen(false)

        // Persist the choice to the backend so a reload does not revert it:
        // AuthContext hydrates i18n from the backend `preferences.language` on
        // login, so without this the nav selection silently reverts. A partial
        // { language } update is safe — the backend MERGES it (currency/date
        // prefs are untouched). Wrapped in its OWN try/catch: a failed persist
        // must NOT undo the already-applied live change (degrades to the prior
        // session-only behavior, no regression).
        try {
          await settingsService.updatePreferences({ language: lng })
        } catch (persistError) {
          logger.error('[LanguageSelector] Failed to persist language:', persistError)
        }

        // Audio feedback if available
        const windowWithAudio = window as unknown as {
          audioFeedback?: { playSuccess: () => void; playError: () => void }
        }
        if (typeof window !== 'undefined' && windowWithAudio.audioFeedback) {
          windowWithAudio.audioFeedback.playSuccess()
        }
      } catch (error) {
        logger.error('[LanguageSelector] Failed to change language:', error)

        // Audio feedback for error if available
        const windowWithAudio = window as unknown as {
          audioFeedback?: { playSuccess: () => void; playError: () => void }
        }
        if (typeof window !== 'undefined' && windowWithAudio.audioFeedback) {
          windowWithAudio.audioFeedback.playError()
        }
      }
    },
    [currentLanguage, changeLanguage, isLoading],
  )

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  /**
   * Language entries to render. Issue #330: built dynamically from the
   * DB-backed catalog (via `useLanguage().availableLanguages`). Well-known
   * codes (es, en) get their SVG flag; others fall back to GenericFlag.
   */
  const languageEntries: Array<{
    code: string
    label: string
    flag: React.FC<{ className?: string }>
  }> = Object.entries(availableLanguages).map(([code, label]) => ({
    code,
    label,
    flag: FlagByCode[code] ?? GenericFlag,
  }))

  // Legacy compatibility object so the existing render paths below keep working.
  const languageData: Record<
    string,
    { flag: React.FC<{ className?: string }>; label: string; code: string }
  > = Object.fromEntries(languageEntries.map((e) => [e.code, e]))

  // Size classes
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  }

  // Position classes for dropdown
  const positionClasses = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 transform -translate-x-1/2',
  }

  const currentLangData = languageData[currentLanguage]
  const CurrentFlag = currentLangData?.flag ?? GenericFlag

  if (variant === 'buttons') {
    // Button variant (for settings page)
    return (
      <div className={`flex space-x-2 ${className}`}>
        {Object.entries(languageData).map(([code, data]) => {
          const Flag = data.flag
          const isActive = code === currentLanguage

          return (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              disabled={isLoading || isActive}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-lg transition-all
                ${
                  isActive
                    ? 'bg-crypto-accent text-white'
                    : 'bg-crypto-card hover:bg-crypto-border text-crypto-muted hover:text-white'
                }
                ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${sizeClasses[size]}
              `}
            >
              {showFlags && <Flag className="w-4 h-3" />}
              {showLabels && <span>{data.label}</span>}
            </button>
          )
        })}
      </div>
    )
  }

  if (variant === 'flags') {
    // Flags only variant (compact)
    return (
      <div className={`flex space-x-1 ${className}`}>
        {Object.entries(languageData).map(([code, data]) => {
          const Flag = data.flag
          const isActive = code === currentLanguage

          return (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              disabled={isLoading || isActive}
              title={data.label}
              className={`
                p-1 rounded transition-all
                ${isActive ? 'ring-2 ring-crypto-accent' : 'hover:bg-crypto-border/30'}
                ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <Flag className="w-6 h-4" />
            </button>
          )
        })}
      </div>
    )
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        data-testid="language-selector-trigger"
        onClick={toggleDropdown}
        disabled={isLoading}
        className={`
          flex items-center space-x-2 px-3 py-2 rounded-lg
          bg-crypto-card hover:bg-crypto-border/30 transition-colors
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${sizeClasses[size]}
        `}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('common:aria.selectLanguage')}
      >
        {showFlags && <CurrentFlag className="w-4 h-3 flex-shrink-0" />}
        {showLabels && (
          <span className="text-crypto-muted group-hover:text-white transition-colors">
            {currentLangData?.label ?? 'Español'}
          </span>
        )}
        <svg
          className={`w-4 h-4 text-crypto-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          data-testid="language-selector-dropdown"
          className={`
            absolute mt-2 w-48 glass-effect rounded-xl p-2 z-50
            transform transition-all duration-200
            ${positionClasses[position]}
          `}
        >
          <div className="space-y-1">
            {Object.entries(languageData).map(([code, data]) => {
              const Flag = data.flag
              const isActive = code === currentLanguage

              return (
                <button
                  key={code}
                  data-testid={`language-selector-option-${code}`}
                  onClick={() => handleLanguageChange(code)}
                  disabled={isLoading || isActive}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all
                    ${
                      isActive
                        ? 'bg-crypto-accent text-white'
                        : 'text-crypto-muted hover:text-white hover:bg-crypto-card/50'
                    }
                    ${isLoading || isActive ? 'cursor-not-allowed' : 'cursor-pointer'}
                    ${sizeClasses[size]}
                  `}
                >
                  <Flag className="w-5 h-4 flex-shrink-0" />
                  <span className="flex-1">{data.label}</span>
                  {isActive && (
                    <svg
                      className="w-4 h-4 text-crypto-accent"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          {/* Footer with current locale info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-2 pt-2 border-t border-crypto-border">
              <div className="px-4 py-2 text-xs text-crypto-muted">
                Current: {currentLanguage.toUpperCase()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-crypto-card/50 rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-crypto-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

LanguageSelector.displayName = 'LanguageSelector'
